import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import tc from 'tinycolor2';
import withData from '@bd/bdcore-data';
import { css, cx } from '@bd/bdui-styles/emotion';
import { Link } from '@bd/bdcore-named-routes';
import ImageWrapper from './ImageWrapper';
// import IconEnvelopeSolid from './IconEnvelopeSolid';
// import IconLink from './IconLink';
import { colors, typography } from '../styles';

const styles = css`
  width: 100%;
  position: relative;
  padding-bottom: 15px;
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: ${tc(colors.color3).darken(5).toString()};
  }
  @media (min-width: 768px) {
    max-width: 345px;
    flex: 1 0 33.333333%;
    width: auto;
    padding-bottom: 42px;
    background-color: ${tc.mix(colors.color3, 'white', 50).toString()};
    &::after {
      content: none;
    }
  }
  @media (min-width: 1280px) {
    max-width: 320px;
  }
  @media (min-width: 1850px) {
    max-width: 345px;
  }
  .AgentCard_imageWrapper {
    width: 112px;
    height: 149px;
    margin-right: 14px;
    margin-bottom: 12px;
    float: left;
    @media (min-width: 768px) {
      width: 124px;
      height: 165px;
      float: none;
      margin: -35px auto 24px;
    }
  }
  .AgentCard_textWrapper {
    margin-bottom: 16px;
    &::after {
      content: '';
      display: table;
      clear: both;
    }
    @media (min-width: 768px) {
      text-align: center;
      padding: 0 12px;
      &::after {
        content: none;
      }
    }
  }
  .AgentCard_nameWrapper {
    @media (min-width: 768px) {
      margin-bottom: 18px;
    }
  }
  .AgentCard_name {
    font-family: ${typography.SansSerif};
    font-size: 24px;
    font-weight: normal;
    line-height: 1.1;
    color: ${colors.color4};
    margin-bottom: 0;
    word-break: break-word;
    hyphens: auto;
    @media (min-width: 1280px) {
      font-size: 26px;
    }
  }
  .AgentCard_title {
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 0.04em;
    line-height: 1.1;
    text-transform: uppercase;
    margin-top: 0.4em;
    margin-bottom: 0;
    @media (min-width: 768px) {
      font-size: 12px;
    }
    @media (min-width: 1280px) {
      font-size: 13px;
    }
  }
  .AgentCard_contactInfo {
    font-size: 14px;
    font-weight: 500;
    margin-top: 16px;
    @media (min-width: 768px) {
      margin-top: 0;
    }
  }
  .AgentCard_phone {
    display: block;
  }
  .AgentCard_linksWrapper {
    display: flex;
    align-items: center;
    margin-top: 10px;
    > :not(:first-child) {
      margin-left: 16px;
    }
    @media (min-width: 768px) {
      justify-content: center;
    }
  }
  .AgentCard_email,
  .AgentCard_website {
    display: flex;
    align-items: center;
    font-weight: bold;
    color: ${colors.color1};
    &:focus {
      outline: 1px dashed ${colors.color3};
    }
    svg {
      display: inline-block;
      width: 16px;
      margin-right: 6px;
    }
    @media (min-width: 768px) {
      svg {
        width: 14px;
        margin-right: 4px;
      }
    }
    @media (min-width: 992px) {
      &:hover {
        color: ${colors.color1Light};
        svg path {
          fill: ${tc(colors.color3).lighten(15).toString()};
        }
      }
    }
  }
  .AgentCard_detailLinkWrapper {
    @media (min-width: 768px) {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 42px;
    }
  }
  .AgentCard_detailLink {
    display: flex;
    justify-content: flex-end;
    font-size: 13px;
    font-weight: bold;
    line-height: 1.25;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.color1Light};
    &:focus {
      outline: 1px dashed ${colors.color3};
    }
    @media (min-width: 768px) {
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 100%;
      padding: 0 26px;
      font-size: 15px;
      background-color: ${tc(colors.color3).setAlpha(0.8).toString()};
    }
    @media (min-width: 992px) {
      transition: background-color 250ms ease;
      &:hover {
        background-color: ${tc(colors.color3).darken(3).saturate(8).toString()};
      }
    }
  }
  .AgentCard_detailLinkIcon {
    display: block;
    width: 16px;
    height: auto;
    margin-left: 12px;
    @media (min-width: 768px) {
      width: 20px;
      margin-left: 0;
    }
  }
`;

function AgentCard({ agent }) {
  const title = get(agent, 'title');
  const phone = get(agent, 'telephone');
  const secondaryPhone = get(agent, 'secondary_phone');
  const email = get(agent, 'email');
  const website = get(agent, 'website');
  const phoneRegex = /\D*(\d{3})\D*(\d{3})\D*(\d{4})/;
  return (
    <div className={cx('AgentCard', styles)}>
      <ImageWrapper
        className="AgentCard_imageWrapper"
        image={{
          src: get(agent, 'logo.contentUrl'),
          alt: '',
          width: 200,
          height: 200,
        }}
        fallback={{
          src: '/images/agent-placeholder.jpg',
          alt: '',
        }}
      />
      <div className="AgentCard_textWrapper">
        <div className="AgentCard_nameWrapper">
          <h4 className="AgentCard_name">{get(agent, 'name')}</h4>
          {
            title &&
            <h5 className="AgentCard_title">{title}</h5>
          }
        </div>
        <div className="AgentCard_contactInfo">
          {
            phone &&
            <span className="AgentCard_phone">
              {`Main: ${phone.replace(phoneRegex, '($1) $2-$3')}`}
            </span>
          }
          {
            secondaryPhone &&
            <span className="AgentCard_phone">
              {`Alt: ${secondaryPhone.replace(phoneRegex, '($1) $2-$3')}`}
            </span>
          }
          {
            (email || website) &&
            <div className="AgentCard_linksWrapper">
              {
                email &&
                <a
                  className="AgentCard_email"
                  href={`mailto:${get(agent, 'email')}`}
                >Email</a>
              }
              {
                website &&
                <a
                  className="AgentCard_website"
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                >Website</a>
              }
            </div>
          }
        </div>
      </div>
      <div className="AgentCard_detailLinkWrapper">
        <Link className="AgentCard_detailLink" name="agent-detail" params={{ agentId: agent._id }}>
          Agent Details
          <img className="AgentCard_detailLinkIcon" src="/images/icon-arrow-right-dark.svg" alt="" />
        </Link>
      </div>
    </div>
  );
}

AgentCard.propTypes = {
  agent: PropTypes.object.isRequired,
};

export default withData({
  agent: true,
})(AgentCard);



// WEBPACK FOOTER //
// ./src/components/AgentCard.js